/**
 * Project-wide SEO content parser — single source of truth for turning
 * raw content blocks (which may contain Mustache template tokens like
 * `{{ teamName }}`) into the plain-string shape that
 * `$lib/components/common/SEO/SEO.svelte` expects.
 *
 * Pages just pick the right raw content key and optionally supply a view
 * object with values to interpolate. Tournament-level values that every
 * page needs (e.g. `{{ tournamentName }}`, `{{ venue }}`) are pulled
 * automatically from `content.tournamment[<active tournament type>]`.
 *
 * Any `{{ token }}` that can't be resolved against the merged view is
 * left in the output verbatim, so downstream pages / share-card
 * generators can finish substituting them.
 */

import Mustache from 'mustache';
import settings from '$lib/config/settings.json';

/**
 * The SEO field list. Kept in one place so a future field addition only
 * needs to update this constant and the matching `RawSeo` interface in
 * `index.d.ts`.
 */
const SEO_FIELDS = [
  'seoTitle',
  'seoDescription',
  'shareTitle',
  'shareDescription',
  'shareImgPath',
  'shareImgAlt',
];

/**
 * Pull the common tournament-level view from a content object.
 *
 * `content.tournamment` is keyed by tournament type (`wc`, `euro`) and
 * exposes labels like `name`, `venue`, `year`, `game`. SEO templates
 * reference these via `{{ tournamentName }}`, `{{ venue }}`, etc.
 *
 * @param {Record<string, any> | undefined} content
 * @returns {Record<string, string | undefined>}
 */
const getCommonView = (content) => {
  const type = /** @type {'wc' | 'euro'} */ (settings.tournament.type);

  const t = content?.tournamment?.[type];
  return {
    tournamentName: `${t?.prefix} ${t?.game}`,
    tournamentYear: t?.year,
    tournamentKey: t?.key,
    venue: t?.venue,
  };
};

/**
 * Wrap a plain view object so Mustache leaves unknown tokens unchanged.
 *
 * By default Mustache substitutes missing values with an empty string,
 * which would silently strip placeholders that downstream consumers
 * (e.g. share-card pages, server-side renderers) want to fill in later.
 * This Proxy advertises every key as present and, when asked for an
 * unknown / nullish one, returns the original `{{ token }}` string so
 * the template token survives the render pass.
 *
 * @param {Record<string, unknown>} view
 * @returns {Record<string, unknown>}
 */
const preserveUnresolved = (view) =>
  new Proxy(view, {
    has: () => true,
    get(target, key) {
      if (typeof key !== 'string') {
        return Reflect.get(target, key);
      }
      const value = target[key];
      if (value === undefined || value === null || value === '') {
        return `{{ ${key} }}`;
      }
      return value;
    },
  });

/**
 * Render every SEO field through Mustache so the shared SEO wrapper
 * just sees plain strings.
 *
 * HTML-escaping is disabled so values containing characters like
 * apostrophes (e.g. team names like "Cote d'Ivoire") aren't turned into
 * entities like `&#39;`, which would break URLs in `shareImgPath`.
 *
 * Behaviour:
 *  - Common tournament view (e.g. `tournamentName`, `venue`) is merged
 *    in automatically from `content.tournamment[<settings type>]`. Pages
 *    only need to supply page-specific values like `teamName` or `homeTeam`.
 *  - Any `{{ token }}` that can't be resolved against the merged view is
 *    preserved verbatim in the output rather than blanked out, so
 *    downstream pages can finish the substitution.
 *
 * @param {Record<string, any> | undefined} content - The full content
 *   object (e.g. `data.content`). Used both to look up the requested SEO
 *   block and to pull the common tournament view.
 * @param {string | null} [key] - Nested content key for the SEO block
 *   (e.g. `'match-seo'`, `'team-seo'`). Pass `null` / omit to read SEO
 *   fields from the top level of `content` (used by the home page).
 * @param {Record<string, string | undefined>} [view] - Additional view
 *   values to interpolate alongside the common tournament view.
 * @returns {import('./index').ParsedSeo}
 *
 * @example
 *   parseSeo(content, 'team-seo', { teamName, teamSlug });
 *   parseSeo(content, 'groups-seo');
 *   parseSeo(content); // home page reads top-level SEO fields
 */
export const parseSeo = (content, key = null, view = {}) => {
  const rawSeo = key ? content?.[key] : content;
  const merged = preserveUnresolved({
    ...getCommonView(content),
    ...view,
  });

  const render = (str) =>
    str ?
      Mustache.render(str, merged, undefined, {
        escape: (v) => v,
      })
    : '';

  return Object.fromEntries(SEO_FIELDS.map((k) => [k, render(rawSeo?.[k])]));
};

export default parseSeo;
